import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, QrCode } from 'lucide-react';
import clockingMachineModeImage from '../assets/clocking-machine-mode.png';

export default function ClockingMachineModeArticle({ headingAs = 'h2' }) {
  const { t } = useTranslation();
  const HeadingTag = headingAs;

  const clockingMachineBenefits = [
    t('guide.clockingMachineBenefit1', 'No need to invest in new hardware'),
    t('guide.clockingMachineBenefit2', 'Use tablets and phones you already own'),
    t('guide.clockingMachineBenefit3', 'Add more terminals as your business grows'),
    t('guide.clockingMachineBenefit4', 'Reduce waste by reusing old technology'),
  ];

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-5 overflow-hidden">
      <div className="flex items-center gap-3 mb-3">
        <QrCode size={22} className="text-cyan-400" />
        <HeadingTag className="text-lg font-semibold text-white">
          {t('guide.clockingMachineModeTitle', 'Clocking Machine Mode: How to Turn Old Devices into Staff Clock-In Terminals')}
        </HeadingTag>
      </div>

      <p className="text-sm text-gray-300 leading-relaxed mb-4">
        {t(
          'guide.clockingMachineModeIntro',
          'Many businesses assume better attendance tracking means buying new hardware. In reality, old tablets, unused smartphones, and spare devices can be repurposed into fixed staff clock-in terminals with BuildMyShifts.'
        )}
      </p>

      <img
        src={clockingMachineModeImage}
        alt={t('guide.clockingMachineModeImageAlt', 'BuildMyShifts Clocking Machine Mode on a tablet terminal')}
        className="w-full rounded-xl border border-cyan-500/20 mb-5"
      />

      <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-white mb-2">
              {t('guide.clockingMachineModeWhyTitle', 'Why this matters')}
            </h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              {t(
                'guide.clockingMachineModeWhyDesc',
                'Manual attendance tracking, paper systems, and inconsistent processes create inaccurate records, wasted admin time, and less accountability. A simple shared clock-in terminal fixes that without adding more hardware costs.'
              )}
            </p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white mb-2">
              {t('guide.clockingMachineModeDevicesTitle', 'Use the devices you already own')}
            </h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              {t(
                'guide.clockingMachineModeDevicesDesc',
                'Mount an older iPad, Android tablet, or spare smartphone near an entrance, staff room, or reception desk. The device becomes a central clocking point for the whole team.'
              )}
            </p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white mb-2">
              {t('guide.clockingMachineModeHowTitle', 'How BuildMyShifts helps')}
            </h3>
            <p className="text-xs text-gray-400 leading-relaxed mb-3">
              {t(
                'guide.clockingMachineModeHowDesc',
                'Clocking Machine Mode lets staff clock in and out with QR cards or staff PINs from one shared device, while managers get real-time attendance visibility, reliable records, and better staffing control.'
              )}
            </p>

            <div className="space-y-3">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex gap-3 items-start bg-white/5 rounded-lg p-3 border border-white/5">
                  <div className="w-7 h-7 rounded-full bg-cyan-500/15 text-cyan-300 flex items-center justify-center text-sm font-bold">
                    {step}
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-white">{t(`guide.terminalStep${step}Title`)}</h4>
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed">{t(`guide.terminalStep${step}Desc`)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-slate-950/40 rounded-xl p-4 border border-cyan-500/15">
            <h3 className="text-sm font-semibold text-white mb-3">
              {t('guide.clockingMachineModeBenefitsTitle', 'Benefits of this approach')}
            </h3>
            <div className="space-y-3">
              {clockingMachineBenefits.map((benefit) => (
                <div key={benefit} className="flex items-start gap-2">
                  <CheckCircle2 size={16} className="text-cyan-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-gray-300 leading-relaxed">{benefit}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-slate-950/40 rounded-xl p-4 border border-cyan-500/15">
            <h3 className="text-sm font-semibold text-white mb-2">
              {t('guide.clockingMachineModeUseCaseTitle', 'Real-world use case')}
            </h3>
            <p className="text-xs text-gray-400 leading-relaxed">
              {t(
                'guide.clockingMachineModeUseCaseDesc',
                'A hospitality business can place one tablet by the staff entrance so everyone clocks in and out from the same device. That creates a consistent process, accurate attendance records, and no need for extra hardware or separate staff logins.'
              )}
            </p>
          </div>

          <div className="bg-cyan-500/10 rounded-xl p-4 border border-cyan-500/20">
            <h3 className="text-sm font-semibold text-white mb-2">
              {t('guide.clockingMachineModeFinalTitle', 'Final thoughts')}
            </h3>
            <p className="text-xs text-gray-300 leading-relaxed">
              {t(
                'guide.clockingMachineModeFinalDesc',
                'You do not always need to buy more to improve staff management. Clocking Machine Mode helps you turn existing devices into practical staff clock-in terminals so you can save money, reduce waste, and improve attendance tracking with the tools you already have.'
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
